/**
 * Mailer Template List Hook
 */
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getMailerTemplates } from '../services'
import {
    getMailerTemplateColumns,
    MAILER_TEMPLATE_FILTER_DEFAULTS,
    MAILER_TEMPLATE_SORT_CONFIG,
} from '../utils/mailer-template-list-config'

interface TemplateFilters {
    q: string
}

export const useMailerTemplateList = () => {
    const { t } = useTranslation()

    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams<TemplateFilters>({
            defaults: {
                page: 1,
                page_size: 20,
                ...MAILER_TEMPLATE_SORT_CONFIG.defaultSort,
            },
            filterDefaults: MAILER_TEMPLATE_FILTER_DEFAULTS,
        })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MAILER_TEMPLATES, queryParams],
        queryFn: () =>
            getMailerTemplates({
                page: params.page,
                page_size: params.page_size,
                search: queryParams.q,
            }),
        placeholderData: true,
    })

    const templates = data?.results || []
    const pagination = data?.pagination

    const columns = useMemo(() => getMailerTemplateColumns(t), [t])

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    return {
        templates,
        pagination,
        stateComponent: renderState(),
        params,
        searchInputProps,
        columns,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        t,
    }
}
